import * as React from 'react';

import { uniq } from 'lodash';

import CoursePartition from 'bundles/internal-enterprise/components/CoursePartition';
import S12nPartition from 'bundles/internal-enterprise/components/S12nPartition';
import constants from 'bundles/internal-enterprise/constants';
import type { ComposedProgram, EnrollItem } from 'bundles/internal-enterprise/types/Program';
import Card from 'bundles/internal-ui/components/cards/Card';
import Naptime from 'bundles/naptimejs';
import type EnterpriseProgramsV1 from 'bundles/naptimejs/resources/enterprisePrograms.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import ProgramCurriculumProductsV1 from 'bundles/naptimejs/resources/programCurriculumProducts.v1';
import type ProgramCurriculumsV1 from 'bundles/naptimejs/resources/programCurriculums.v1';

import 'css!./__styles__/EnterpriseToolsHome';

type InputProps = {
  program: EnterpriseProgramsV1;
  curriculum: ProgramCurriculumsV1;
  userId: string;
};

type Props = InputProps & {
  enrolledProducts: Array<ProgramCurriculumProductsV1>;
};

type State = {
  coursePartitions: Array<Array<EnrollItem>>;
  s12nPartitions: Array<Array<EnrollItem>>;
  composedProgram: ComposedProgram;
};

class ProgramCurriculum extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const { program, enrolledProducts, curriculum } = this.props;
    const { coreTracks = [], electiveTracks = [], allCourseIds = [] } = curriculum?.definition ?? {};
    const curriculumItems = coreTracks
      .concat(electiveTracks)
      .map((track: $TSFixMe) => track.curriculumItems)
      .reduce((allItems: $TSFixMe, items: $TSFixMe) => allItems.concat(items), []);
    const s12ns = (curriculumItems as Array<any>)
      .filter((item) => item.typeName === 's12nId')
      .map((item) => ({
        id: item.definition.id,
        enrolled: enrolledProducts.some((product) => product.productItemId === item.definition.id),
      }));
    const courseIds: Array<string> = uniq(allCourseIds);
    const courses = courseIds.map((id) => ({
      id,
      enrolled: enrolledProducts.some((product) => product.productItemId === id),
    }));

    const composedProgram = {
      id: program.id,
      name: program.name,
      slug: program.slug,
      courses,
      s12ns,
    };

    // Partition courses and s12ns into smaller groups to reduce request URI length
    const coursePartitions = courses.reduce((partitions, course, index) => {
      const partition = Math.floor(index / constants.partitionSize);
      if (partitions[partition]) {
        partitions[partition].push(course);
      } else {
        // eslint-disable-next-line no-param-reassign
        partitions[partition] = [course];
      }
      return partitions;
    }, [] as EnrollItem[][]);

    const s12nPartitions = s12ns.reduce((partitions, course, index) => {
      const partition = Math.floor(index / constants.partitionSize);
      if (partitions[partition]) {
        partitions[partition].push(course);
      } else {
        // eslint-disable-next-line no-param-reassign
        partitions[partition] = [course];
      }
      return partitions;
    }, [] as EnrollItem[][]);

    this.state = { composedProgram, s12nPartitions, coursePartitions };
  }

  render() {
    const { userId } = this.props;
    const { coursePartitions, s12nPartitions, composedProgram } = this.state;

    return (
      <div className="rc-ProgramCurriculum">
        <Card flexibleHeight={true}>
          <div className="card-header-row">
            <h2 className="card-header-item">{composedProgram.name}</h2>
            <p className="card-header-item">
              <b>Program ID:</b> {composedProgram.id}
            </p>
            <p className="card-header-item">
              <b>Program Slug:</b> {composedProgram.slug}
            </p>
          </div>
          {composedProgram.s12ns.length > 0 && (
            <div className="card-block">
              <h3>Specializations</h3>
              <div className="table col-xs-12">
                <div className="row-block">
                  <div className="row">
                    <div className="table-cell col-xs-3">Name</div>
                    <div className="table-cell col-xs-3">Slug</div>
                    <div className="table-cell col-xs-3">S12n ID</div>
                    <div className="table-cell col-xs-2">Enrollment Status</div>
                    <div className="table-cell col-xs-1">Enroll Learner</div>
                  </div>
                </div>
                {s12nPartitions.map((partition, index) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <S12nPartition key={index} s12nItems={partition} programId={composedProgram.id} userId={userId} />
                ))}
              </div>
            </div>
          )}
          {composedProgram.courses.length > 0 && (
            <div className="card-block">
              <h3>Courses</h3>
              <div className="table col-xs-12">
                <div className="row-block">
                  <div className="row">
                    <div className="table-cell col-xs-3">Name</div>
                    <div className="table-cell col-xs-2">Slug</div>
                    <div className="table-cell col-xs-2">Course ID</div>
                    <div className="table-cell col-xs-2">S12ns Containing Course</div>
                    <div className="table-cell col-xs-2">Enrollment Status</div>
                    <div className="table-cell col-xs-1">Enroll Learner</div>
                  </div>
                </div>
                {coursePartitions.map((partition, index) => (
                  <CoursePartition
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    courseItems={partition}
                    programS12nIds={composedProgram.s12ns.map((s12n) => s12n.id)}
                    programId={composedProgram.id}
                    userId={userId}
                  />
                ))}
              </div>
            </div>
          )}
        </Card>
      </div>
    );
  }
}

export default Naptime.createContainer<Props, InputProps>(ProgramCurriculum, ({ program, userId }) => ({
  enrolledProducts: ProgramCurriculumProductsV1.finder('enrolled', {
    params: {
      programId: program.id,
      userId,
    },
  }),
}));
